/**
 * Visitor Tracking Utilities
 * Handles unique visitor identification and activity logging
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const VISITOR_ID_KEY = 'museum_visitor_id';

/**
 * Generate a unique visitor ID using browser fingerprinting
 * Combines timestamp, random number, and browser characteristics
 */
const generateVisitorId = () => {
  const timestamp = Date.now().toString(36);
  const randomPart = Math.random().toString(36).substring(2, 10);
  const screenInfo = `${window.screen.width}x${window.screen.height}`;
  
  // Create a simple hash from browser info
  const browserInfo = `${navigator.userAgent.length}${navigator.language}`;
  const hash = browserInfo.split('').reduce((a, b) => {
    a = ((a << 5) - a) + b.charCodeAt(0);
    return a & a;
  }, 0).toString(36);
  
  return `${timestamp}-${randomPart}-${hash}`;
};

/**
 * Get or create a visitor ID stored in localStorage
 */
export const getVisitorId = () => {
  try {
    let visitorId = localStorage.getItem(VISITOR_ID_KEY);
    
    if (!visitorId) {
      visitorId = generateVisitorId();
      localStorage.setItem(VISITOR_ID_KEY, visitorId);
    }
    
    return visitorId;
  } catch (e) {
    // localStorage might be blocked (private browsing, etc.)
    return generateVisitorId();
  }
};

/**
 * Initialize or update visitor session for the day
 */
export const initVisitorSession = async () => {
  try {
    const visitorId = getVisitorId();
    const response = await fetch(`${API}/visitor/session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ visitor_id: visitorId })
    });
    
    if (response.ok) {
      const data = await response.json();
      return data;
    } else {
      console.error('Session API returned:', response.status);
    }
  } catch (error) {
    console.error('Failed to init visitor session:', error);
  }
  return null;
};

/**
 * Log visitor activity (viewing an exhibit or zone)
 * @param {string} itemType - 'exhibit' or 'zone'
 * @param {string} itemId - The ID of the exhibit or zone
 * @param {string} itemCode - The 3-digit code
 * @param {string} itemTitle - The title for reference
 */
export const logVisitorActivity = async (itemType, itemId, itemCode, itemTitle) => {
  // Validate inputs
  if (!itemType || !itemId || !itemCode) {
    console.error('logVisitorActivity: Missing required parameters', { itemType, itemId, itemCode });
    return false;
  }

  try {
    const visitorId = getVisitorId();
    console.log(`[Tracking] Starting log for ${itemType} ${itemCode}, visitor: ${visitorId.substring(0, 20)}...`);
    
    // First ensure session exists
    const sessionResult = await initVisitorSession();
    console.log(`[Tracking] Session result:`, sessionResult ? 'OK' : 'FAILED');
    
    // Then log the activity
    const response = await fetch(`${API}/visitor/log`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        visitor_id: visitorId,
        item_type: itemType,
        item_id: itemId,
        item_code: itemCode,
        item_title: itemTitle || ''
      })
    });
    
    if (response.ok) {
      console.log(`[Tracking] SUCCESS: ${itemType} ${itemCode} logged`);
      return true;
    } else {
      console.error(`[Tracking] FAILED: API returned ${response.status}`);
    }
  } catch (error) {
    console.error('[Tracking] ERROR:', error.message || error);
  }
  return false;
};
